<template>
  <CurrentEventDataProvider v-slot="{ hasFullEventDetails }">
    <div
      class="sponsor-page"
      style="
        background-color: var(--background-color, #f3f4f6);
        min-height: 100vh;
        font-family: 'Poppins', sans-serif;
      "
    >
      <Navbar :show-nav="true" />

      <div
        v-if="hasFullEventDetails"
        class="sp-wrap"
        id="main-content"
        role="main"
      >
        <SponsorDataProvider
          v-slot="{ sponsor, documents, polls, users, loading }"
        >
          <StreamDataProvider
            :set-from-sponsor="sponsor"
            v-slot="{
              isReady,
              isImage,
              hasStream,
              embedUrl,
              embedType,
              iframeAllowAttributes
            }"
          >
            <div v-if="!loading && sponsor" class="sp-grid">
              <!-- ── LEFT: header + video player ─────────────── -->
              <div class="sp-left">
                <!-- Header row -->
                <div class="sp-header">
                  <button
                    class="sp-header__back"
                    @click="goBack"
                    :aria-label="$t('Views.Sponsor.back')"
                  >
                    <i class="fas fa-arrow-left"></i>
                  </button>
                  <div class="sp-header__logo-wrap" v-if="sponsor.logoUrl">
                    <img
                      :src="sponsor.logoUrl"
                      :alt="sponsor.name"
                      class="sp-header__logo"
                    />
                  </div>
                  <div class="sp-header__title-block">
                    <h1 class="sp-header__title">{{ sponsor.name }}</h1>
                    <p v-if="sponsor.tagline" class="sp-header__sub">
                      {{ sponsor.tagline }}
                    </p>
                    <p v-else-if="sponsor.address" class="sp-header__sub">
                      <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                      {{ sponsor.address }}
                    </p>
                  </div>
                  <button
                    v-if="sponsor.email"
                    class="sp-header__action"
                    @click="sendContactInfo()"
                    :aria-label="$t('Views.Sponsor.sendInfo')"
                  >
                    <i class="far fa-share-square"></i>
                    <span class="sp-header__action-label">{{
                      $t('Views.Sponsor.sendMyCard')
                    }}</span>
                  </button>
                </div>

                <!-- Video player area -->
                <div class="sp-player">
                  <div class="sp-player__video">
                    <StageStream
                      v-if="hasStream && isReady && embedUrl"
                      :isReady="isReady"
                      :isImage="isImage"
                      :hasStream="hasStream"
                      :embedUrl="embedUrl"
                      :embedType="embedType"
                      :iframeAllowAttributes="iframeAllowAttributes"
                      class="sp-player__stream"
                    />
                    <div
                      v-else-if="sponsor.logoUrl"
                      class="sp-player__logo-fallback"
                    >
                      <img :src="sponsor.logoUrl" :alt="sponsor.name" />
                    </div>
                    <div v-else class="sp-player__placeholder">
                      <div class="sp-player__eye">
                        <i class="far fa-building"></i>
                      </div>
                      <div class="sp-player__active-text">
                        {{ $t('Views.Sponsor.noStreamAvailable') }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- About section under the player -->
                <div v-if="sponsor.description" class="sp-about">
                  <h2 class="sp-about__title">
                    {{
                      $t('Views.Sponsor.aboutSponsor', { name: sponsor.name })
                    }}
                  </h2>
                  <div
                    class="sp-about__body html-content"
                    v-html="sponsor.description"
                  />
                </div>
              </div>

              <!-- ── RIGHT: Tabs sidebar ───────────────────────── -->
              <div class="sp-side">
                <div class="sp-side__main">
                  <div class="sp-side__content">
                    <!-- INFO tab -->
                    <div
                      v-if="activeTab === 'info'"
                      class="sp-panel"
                      id="sp-panel-info"
                      role="tabpanel"
                      aria-labelledby="sp-tab-info"
                    >
                      <div class="sp-info">
                        <h3 class="sp-info__title">
                          {{ $t('Views.Sponsor.contactAndDetails') }}
                        </h3>

                        <div class="sp-info__list">
                          <div v-if="sponsor.address" class="sp-info-row">
                            <div
                              class="sp-info-row__icon sp-info-row__icon--orange"
                            >
                              <i class="fas fa-map-marker-alt"></i>
                            </div>
                            <div class="sp-info-row__body">
                              <div class="sp-info-row__label">
                                {{ $t('Views.Sponsor.address') }}
                              </div>
                              <div class="sp-info-row__value">
                                {{ sponsor.address }}
                              </div>
                            </div>
                          </div>

                          <div v-if="sponsor.phoneNumber" class="sp-info-row">
                            <div
                              class="sp-info-row__icon sp-info-row__icon--green"
                            >
                              <i class="fas fa-phone"></i>
                            </div>
                            <div class="sp-info-row__body">
                              <div class="sp-info-row__label">
                                {{
                                  $t(
                                    'Components.Speaker.SpeakerInformation.phoneLabel'
                                  )
                                }}
                              </div>
                              <a
                                :href="'tel:' + sponsor.phoneNumber"
                                class="sp-info-row__link"
                              >
                                {{ sponsor.phoneNumber }}
                              </a>
                            </div>
                          </div>

                          <div v-if="sponsor.email" class="sp-info-row">
                            <div
                              class="sp-info-row__icon sp-info-row__icon--teal"
                            >
                              <i class="far fa-envelope"></i>
                            </div>
                            <div class="sp-info-row__body">
                              <div class="sp-info-row__label">
                                {{
                                  $t(
                                    'Components.Speaker.SpeakerInformation.emailLabel'
                                  )
                                }}
                              </div>
                              <a
                                :href="'mailto:' + sponsor.email"
                                class="sp-info-row__link"
                              >
                                {{ sponsor.email }}
                              </a>
                            </div>
                          </div>

                          <div v-if="sponsor.website" class="sp-info-row">
                            <div
                              class="sp-info-row__icon sp-info-row__icon--purple"
                            >
                              <i class="fas fa-globe"></i>
                            </div>
                            <div class="sp-info-row__body">
                              <div class="sp-info-row__label">
                                {{
                                  $t(
                                    'Components.Speaker.SpeakerInformation.websiteLabel'
                                  )
                                }}
                              </div>
                              <a
                                :href="normalizeUrl(sponsor.website)"
                                target="_blank"
                                rel="noopener"
                                class="sp-info-row__link"
                              >
                                {{ sponsor.website }}
                              </a>
                            </div>
                          </div>

                          <!-- Links added by the admin — brand icons for
                               known networks, a globe for anything else. -->
                          <div
                            v-if="socialLinksFor(sponsor).length"
                            class="sp-social"
                          >
                            <a
                              v-for="(l, li) in socialLinksFor(sponsor)"
                              :key="'sl-' + li"
                              :href="l.href"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="sp-social__link"
                              :class="{ 'sp-social__link--brand': l.brand }"
                              :style="socialLinkStyle(l)"
                              :title="l.label"
                              :aria-label="l.label"
                            >
                              <i :class="l.icon" aria-hidden="true"></i>
                            </a>
                          </div>

                          <div
                            v-if="
                              !sponsor.address &&
                              !sponsor.phoneNumber &&
                              !sponsor.email &&
                              !sponsor.website &&
                              !socialLinksFor(sponsor).length
                            "
                            class="sp-empty"
                          >
                            <i
                              class="far fa-address-card"
                              style="
                                font-size: 28px;
                                color: #d1d5db;
                                margin-bottom: 8px;
                              "
                            ></i>
                            <p>{{ $t('Views.Sponsor.noContactDetails') }}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- DOCS tab -->
                    <div
                      v-else-if="activeTab === 'docs'"
                      class="sp-panel"
                      id="sp-panel-docs"
                      role="tabpanel"
                      aria-labelledby="sp-tab-docs"
                    >
                      <div class="sp-docs-header">
                        <h3 class="sp-docs-header__title">
                          {{ $t('Views.Sponsor.documents') }}
                        </h3>
                        <p class="sp-docs-header__sub">
                          {{
                            $tc(
                              'Views.Sponsor.filesAvailable',
                              docsList(documents).length,
                              { count: docsList(documents).length }
                            )
                          }}
                        </p>
                      </div>
                      <div class="sp-docs-list">
                        <div
                          v-for="doc in docsList(documents)"
                          :key="doc.DocumentID"
                          class="sp-doc-card"
                        >
                          <div class="sp-doc-card__row">
                            <div
                              class="sp-doc-card__icon"
                              :class="docIconClass(doc)"
                            >
                              <i :class="docIcon(doc)"></i>
                            </div>
                            <div class="sp-doc-card__info">
                              <div class="sp-doc-card__name-row">
                                <span
                                  class="sp-doc-card__name"
                                  :title="doc.Name"
                                  >{{ doc.Name }}</span
                                >
                                <button
                                  type="button"
                                  class="sp-doc-card__mail"
                                  :class="{
                                    'sp-doc-card__mail--sending':
                                      isSendingDoc(doc)
                                  }"
                                  :disabled="!docUrl(doc) || isSendingDoc(doc)"
                                  @click.stop="emailDocument(doc)"
                                  :aria-label="
                                    $t('Views.Sponsor.emailDocumentToMe')
                                  "
                                  :title="
                                    isSendingDoc(doc)
                                      ? $t('Views.Sponsor.sendingDocument')
                                      : $t('Views.Sponsor.emailMeACopy')
                                  "
                                >
                                  <i
                                    v-if="isSendingDoc(doc)"
                                    class="fas fa-circle-notch fa-spin"
                                  ></i>
                                  <i v-else class="far fa-envelope"></i>
                                </button>
                              </div>
                              <div class="sp-doc-card__meta">
                                <span class="sp-doc-card__type">{{
                                  docExt(doc)
                                }}</span>
                                <span v-if="doc.Size" class="sp-doc-card__sep"
                                  >•</span
                                >
                                <span v-if="doc.Size">{{
                                  formatBytes(doc.Size)
                                }}</span>
                              </div>
                            </div>
                          </div>
                          <div class="sp-doc-card__actions">
                            <button
                              type="button"
                              class="sp-doc-btn sp-doc-btn--secondary"
                              :disabled="!docUrl(doc)"
                              @click="previewDocument(doc)"
                            >
                              <i class="far fa-eye"></i>
                              {{ $t('Views.Sponsor.preview') }}
                            </button>
                            <button
                              type="button"
                              class="sp-doc-btn sp-doc-btn--primary"
                              :disabled="!docUrl(doc)"
                              @click="downloadDocument(doc)"
                            >
                              <i class="fas fa-download"></i>
                              {{ $t('Views.Sponsor.documentsDownload') }}
                            </button>
                          </div>
                        </div>
                        <div
                          v-if="!docsList(documents).length"
                          class="sp-empty"
                        >
                          <i
                            class="far fa-folder-open"
                            style="
                              font-size: 28px;
                              color: #d1d5db;
                              margin-bottom: 8px;
                            "
                          ></i>
                          <p>{{ $t('Views.Sponsor.noDocuments') }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- POLLS tab -->
                    <div
                      v-else-if="activeTab === 'polls'"
                      class="sp-panel"
                      id="sp-panel-polls"
                      role="tabpanel"
                      aria-labelledby="sp-tab-polls"
                    >
                      <!-- Active poll view ─────────────────────────── -->
                      <template v-if="selectedPoll">
                        <div class="sp-docs-header sp-docs-header--with-back">
                          <button
                            class="sp-back-btn"
                            @click="closeSponsorPoll"
                            :aria-label="$t('Views.Sponsor.backToPolls')"
                          >
                            <i class="fas fa-arrow-left" aria-hidden="true"></i>
                          </button>
                          <div>
                            <h3 class="sp-docs-header__title">
                              {{ $t('Views.Sponsor.pollsAndQuizzes') }}
                            </h3>
                            <p class="sp-docs-header__sub">
                              {{ $t('Views.Sponsor.submitAnswerBelow') }}
                            </p>
                          </div>
                        </div>
                        <div class="sp-poll-body">
                          <Poll
                            :id="String(selectedPoll.id)"
                            :item="selectedPoll"
                            container="Sponsor"
                            :close="closeSponsorPoll"
                          />
                        </div>
                      </template>

                      <!-- Polls list ────────────────────────────────── -->
                      <template v-else>
                        <div class="sp-docs-header">
                          <h3 class="sp-docs-header__title">
                            {{ $t('Views.Sponsor.pollsAndQuizzes') }}
                          </h3>
                          <p class="sp-docs-header__sub">
                            {{
                              $t('Views.Sponsor.pollsAvailable', {
                                count: (polls && polls.length) || 0
                              })
                            }}
                          </p>
                        </div>
                        <div v-if="pollLoading" class="sp-empty">
                          <i
                            class="fas fa-circle-notch fa-spin"
                            style="
                              font-size: 22px;
                              color: var(--color-primary, #047873);
                            "
                          ></i>
                        </div>
                        <div
                          v-else-if="polls && polls.length"
                          class="sp-polls-list"
                        >
                          <button
                            v-for="p in polls"
                            :key="p.value || p.id"
                            class="sp-poll-card"
                            @click="openSponsorPoll(p)"
                          >
                            <div class="sp-poll-card__icon">
                              <i class="fas fa-poll-h"></i>
                            </div>
                            <div class="sp-poll-card__body">
                              <div class="sp-poll-card__title">
                                {{
                                  p.label ||
                                  p.title ||
                                  $t('Views.Sponsor.pollFallback')
                                }}
                              </div>
                              <div class="sp-poll-card__sub">
                                {{ $t('Views.Sponsor.clickToParticipate') }}
                              </div>
                            </div>
                            <i
                              class="fas fa-chevron-right sp-poll-card__chevron"
                            ></i>
                          </button>
                        </div>
                        <div v-else class="sp-empty">
                          <i
                            class="far fa-chart-bar"
                            style="
                              font-size: 28px;
                              color: #d1d5db;
                              margin-bottom: 8px;
                            "
                          ></i>
                          <p>{{ $t('Views.Sponsor.noPolls') }}</p>
                        </div>
                      </template>
                    </div>

                    <!-- PEOPLE tab -->
                    <div
                      v-else-if="activeTab === 'people'"
                      class="sp-panel"
                      id="sp-panel-people"
                      role="tabpanel"
                      aria-labelledby="sp-tab-people"
                    >
                      <div class="sp-docs-header">
                        <h3 class="sp-docs-header__title">
                          {{ $t('Views.Sponsor.team') }}
                        </h3>
                        <p class="sp-docs-header__sub">
                          {{
                            $tc(
                              'Views.Sponsor.teamCount',
                              (users && users.length) || 0,
                              { count: (users && users.length) || 0 }
                            )
                          }}
                        </p>
                      </div>
                      <div v-if="users && users.length" class="sp-people-list">
                        <div
                          v-for="user in users"
                          :key="user.UserID || user.id"
                          class="sp-person-card"
                        >
                          <Avatar
                            :image="
                              user.profileImageUrl || user.ProfileImageUrl
                            "
                            :circle="true"
                            :size="'medium'"
                            :alt="$t('Views.Sponsor.teamMember')"
                            class="sp-person-card__avatar"
                          />
                          <div class="sp-person-card__info">
                            <div class="sp-person-card__name">
                              {{ user.Firstname || user.firstName }}
                              {{ user.Lastname || user.lastName }}
                            </div>
                            <div
                              v-if="user.Jobtitle || user.jobTitle"
                              class="sp-person-card__role"
                            >
                              {{ user.Jobtitle || user.jobTitle }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="sp-empty">
                        <i
                          class="far fa-user"
                          style="
                            font-size: 28px;
                            color: #d1d5db;
                            margin-bottom: 8px;
                          "
                        ></i>
                        <p>{{ $t('Views.Sponsor.noTeamMembers') }}</p>
                      </div>
                    </div>

                    <!-- SPONSORS tab — quick-switch list of every
                       active sponsor in the event. Clicking a card
                       routes to that sponsor's live-stream page so
                       attendees can hop between sponsor streams
                       without leaving the panel. -->
                    <div
                      v-else-if="activeTab === 'sponsors'"
                      class="sp-panel"
                      id="sp-panel-sponsors"
                      role="tabpanel"
                      aria-labelledby="sp-tab-sponsors"
                    >
                      <div class="sp-docs-header">
                        <h3 class="sp-docs-header__title">
                          {{ $t('Views.Sponsor.allSponsors') }}
                        </h3>
                        <p class="sp-docs-header__sub">
                          {{
                            $t('Views.Sponsor.sponsorsShown', {
                              shown: filteredOtherSponsors.length,
                              total: otherSponsors.length
                            })
                          }}
                        </p>
                      </div>

                      <!-- Search + category filter (mirrors lobby modal) -->
                      <div class="sp-sponsor-filters">
                        <div class="sp-sponsor-filters__search-wrap">
                          <i
                            class="fas fa-search sp-sponsor-filters__search-icon"
                            aria-hidden="true"
                          ></i>
                          <input
                            v-model="sponsorSearchInput"
                            type="text"
                            class="sp-sponsor-filters__search"
                            :placeholder="
                              $t(
                                'Components.SponsorPickerModal.searchPlaceholder'
                              )
                            "
                            :aria-label="$t('Views.Sponsor.searchSponsors')"
                          />
                        </div>
                        <div
                          v-if="sponsorCategoryOptions.length"
                          class="sp-sponsor-filters__category-wrap"
                          v-click-outside="closeSponsorCategoryMenu"
                        >
                          <button
                            type="button"
                            class="sp-sponsor-filters__filter-btn"
                            :class="{
                              'sp-sponsor-filters__filter-btn--active':
                                selectedSponsorCategory
                            }"
                            :aria-label="
                              selectedSponsorCategory
                                ? $t('Views.Sponsor.filterActive', {
                                    category: selectedSponsorCategory
                                  })
                                : $t('Views.Sponsor.filterByCategory')
                            "
                            :aria-expanded="
                              sponsorCategoryMenuOpen ? 'true' : 'false'
                            "
                            aria-haspopup="listbox"
                            @click="
                              sponsorCategoryMenuOpen = !sponsorCategoryMenuOpen
                            "
                          >
                            <i class="fas fa-filter" aria-hidden="true"></i>
                            <span
                              v-if="selectedSponsorCategory"
                              class="sp-sponsor-filters__filter-dot"
                              aria-hidden="true"
                            ></span>
                          </button>
                          <transition name="sp-cat-menu">
                            <ul
                              v-if="sponsorCategoryMenuOpen"
                              class="sp-sponsor-filters__category-menu"
                              role="listbox"
                              :aria-label="$t('Views.Sponsor.filterByCategory')"
                            >
                              <li>
                                <button
                                  type="button"
                                  class="sp-sponsor-filters__category-item"
                                  :class="{
                                    'sp-sponsor-filters__category-item--selected':
                                      !selectedSponsorCategory
                                  }"
                                  role="option"
                                  :aria-selected="
                                    !selectedSponsorCategory ? 'true' : 'false'
                                  "
                                  @click="setSponsorCategory(null)"
                                >
                                  <span>{{
                                    $t('Views.Sponsor.allCategories')
                                  }}</span>
                                  <i
                                    v-if="!selectedSponsorCategory"
                                    class="fas fa-check"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </li>
                              <li
                                v-for="cat in sponsorCategoryOptions"
                                :key="cat.key"
                              >
                                <button
                                  type="button"
                                  class="sp-sponsor-filters__category-item"
                                  :class="{
                                    'sp-sponsor-filters__category-item--selected':
                                      selectedSponsorCategory === cat.key
                                  }"
                                  role="option"
                                  :aria-selected="
                                    selectedSponsorCategory === cat.key
                                      ? 'true'
                                      : 'false'
                                  "
                                  @click="setSponsorCategory(cat.key)"
                                >
                                  <span>{{ cat.label }}</span>
                                  <i
                                    v-if="selectedSponsorCategory === cat.key"
                                    class="fas fa-check"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </li>
                            </ul>
                          </transition>
                        </div>
                      </div>

                      <div
                        v-if="filteredOtherSponsors.length"
                        class="sp-sponsor-list"
                      >
                        <button
                          v-for="other in filteredOtherSponsors"
                          :key="other.id || other.Id"
                          type="button"
                          class="sp-sponsor-row"
                          :class="{
                            'sp-sponsor-row--active': isCurrentSponsor(other)
                          }"
                          @click="switchToSponsor(other)"
                        >
                          <div class="sp-sponsor-row__logo-wrap">
                            <img
                              v-if="other.logoUrl"
                              :src="other.logoUrl"
                              :alt="
                                other.name ||
                                other.companyName ||
                                $t('Views.Sponsor.sponsorFallback')
                              "
                              class="sp-sponsor-row__logo"
                            />
                            <span
                              v-else
                              class="sp-sponsor-row__logo-fallback"
                              aria-hidden="true"
                            >
                              <i class="far fa-building"></i>
                            </span>
                          </div>
                          <div class="sp-sponsor-row__info">
                            <div class="sp-sponsor-row__name">
                              {{
                                other.name ||
                                other.companyName ||
                                $t('Views.Sponsor.sponsorFallback')
                              }}
                            </div>
                            <div
                              v-if="hasLiveStream(other)"
                              class="sp-sponsor-row__status"
                            >
                              <span
                                class="sp-sponsor-row__live-dot"
                                aria-hidden="true"
                              ></span>
                              {{ $t('Views.Sponsor.liveStream') }}
                            </div>
                            <div
                              v-else-if="other.tagline"
                              class="sp-sponsor-row__tagline"
                            >
                              {{ other.tagline }}
                            </div>
                          </div>
                          <i
                            class="fas fa-chevron-right sp-sponsor-row__chevron"
                            aria-hidden="true"
                          ></i>
                        </button>
                      </div>
                      <div v-else class="sp-empty">
                        <i
                          class="far fa-building"
                          style="
                            font-size: 28px;
                            color: #d1d5db;
                            margin-bottom: 8px;
                          "
                        ></i>
                        <p v-if="otherSponsors.length">
                          {{ $t('Views.Sponsor.noSponsorsMatch') }}
                        </p>
                        <p v-else>{{ $t('Views.Sponsor.noOtherSponsors') }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ICON RAIL — vertical column on the RIGHT edge of
                     the side card, mirroring LiveStream's `.ls-icon-bar`.
                     Each icon swaps the active panel; hover reveals a
                     midnight pill tooltip with the section name. -->
                <nav
                  class="sp-icon-bar"
                  role="tablist"
                  :aria-label="$t('Views.Sponsor.sponsorPanels')"
                  @keydown="onSpTabKeydown"
                >
                  <button
                    type="button"
                    class="sp-icon-bar__btn"
                    :class="{
                      'sp-icon-bar__btn--active': activeTab === 'info'
                    }"
                    role="tab"
                    id="sp-tab-info"
                    :aria-selected="activeTab === 'info' ? 'true' : 'false'"
                    aria-controls="sp-panel-info"
                    :aria-label="$t('Views.Sponsor.tabInfo')"
                    :tabindex="activeTab === 'info' ? 0 : -1"
                    @click="activeTab = 'info'"
                  >
                    <i class="far fa-calendar-alt" aria-hidden="true"></i>
                    <span class="sp-icon-bar__label">{{
                      $t('Views.Sponsor.tabInfo')
                    }}</span>
                  </button>
                  <button
                    type="button"
                    class="sp-icon-bar__btn"
                    :class="{
                      'sp-icon-bar__btn--active': activeTab === 'docs'
                    }"
                    role="tab"
                    id="sp-tab-docs"
                    :aria-selected="activeTab === 'docs' ? 'true' : 'false'"
                    aria-controls="sp-panel-docs"
                    :aria-label="$t('Views.Sponsor.tabDocs')"
                    :tabindex="activeTab === 'docs' ? 0 : -1"
                    @click="activeTab = 'docs'"
                  >
                    <i class="far fa-file-alt" aria-hidden="true"></i>
                    <span class="sp-icon-bar__label">{{
                      $t('Views.Sponsor.tabDocs')
                    }}</span>
                    <span
                      v-if="docsList(documents).length"
                      class="sp-icon-bar__badge"
                      >{{ docsList(documents).length }}</span
                    >
                  </button>
                  <button
                    type="button"
                    class="sp-icon-bar__btn"
                    :class="{
                      'sp-icon-bar__btn--active': activeTab === 'polls'
                    }"
                    role="tab"
                    id="sp-tab-polls"
                    :aria-selected="activeTab === 'polls' ? 'true' : 'false'"
                    aria-controls="sp-panel-polls"
                    :aria-label="$t('Views.Sponsor.polls')"
                    :tabindex="activeTab === 'polls' ? 0 : -1"
                    @click="activeTab = 'polls'"
                  >
                    <i class="fas fa-poll" aria-hidden="true"></i>
                    <span class="sp-icon-bar__label">{{
                      $t('Views.Sponsor.polls')
                    }}</span>
                    <span
                      v-if="polls && polls.length"
                      class="sp-icon-bar__badge"
                      >{{ polls.length }}</span
                    >
                  </button>
                  <button
                    type="button"
                    class="sp-icon-bar__btn"
                    :class="{
                      'sp-icon-bar__btn--active': activeTab === 'people'
                    }"
                    role="tab"
                    id="sp-tab-people"
                    :aria-selected="activeTab === 'people' ? 'true' : 'false'"
                    aria-controls="sp-panel-people"
                    :aria-label="$t('Views.Sponsor.tabPeople')"
                    :tabindex="activeTab === 'people' ? 0 : -1"
                    @click="activeTab = 'people'"
                  >
                    <i class="far fa-user" aria-hidden="true"></i>
                    <span class="sp-icon-bar__label">{{
                      $t('Views.Sponsor.tabPeople')
                    }}</span>
                    <span
                      v-if="users && users.length"
                      class="sp-icon-bar__badge"
                      >{{ users.length }}</span
                    >
                  </button>
                  <button
                    type="button"
                    class="sp-icon-bar__btn"
                    :class="{
                      'sp-icon-bar__btn--active': activeTab === 'sponsors'
                    }"
                    role="tab"
                    id="sp-tab-sponsors"
                    :aria-selected="activeTab === 'sponsors' ? 'true' : 'false'"
                    aria-controls="sp-panel-sponsors"
                    :aria-label="$t('Views.Sponsor.tabSponsors')"
                    :tabindex="activeTab === 'sponsors' ? 0 : -1"
                    @click="activeTab = 'sponsors'"
                  >
                    <i class="fas fa-star" aria-hidden="true"></i>
                    <span class="sp-icon-bar__label">{{
                      $t('Views.Sponsor.tabSponsors')
                    }}</span>
                    <span
                      v-if="otherSponsors.length"
                      class="sp-icon-bar__badge"
                      >{{ otherSponsors.length }}</span
                    >
                  </button>
                </nav>
              </div>
            </div>

            <div v-else class="sp-loading">
              <i
                class="fas fa-circle-notch fa-spin"
                style="font-size: 24px; color: var(--color-primary, #047873)"
              ></i>
            </div>
          </StreamDataProvider>
        </SponsorDataProvider>
      </div>

      <div v-else class="sp-loading">
        <i
          class="fas fa-circle-notch fa-spin"
          style="font-size: 24px; color: var(--color-primary, #047873)"
        ></i>
      </div>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ApiClient from '@/client';
import Navbar from '@/components/shared/Navbar';
import Avatar from '@/components/shared/Avatar';
import StageStream from '@/components/shared/StageStream';
import Poll from '@/components/timeline/promoted/Poll';
import BookMeetingModal from '@/components/sponsor/BookMeetingModal';
import { createAnalyticsEvent } from '@/helpers/ga-helper';
import { socialLinkMeta } from '@/helpers/social-links';

// Local click-outside directive — used to close the sponsor-list
// category filter popover when the user clicks anywhere else.
const clickOutsideDirective = {
  bind(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        if (typeof binding.value === 'function') binding.value(event);
      }
    };
    document.addEventListener('click', el.__clickOutsideHandler__);
  },
  unbind(el) {
    document.removeEventListener('click', el.__clickOutsideHandler__);
    delete el.__clickOutsideHandler__;
  }
};

export default {
  name: 'Sponsor',
  components: { Navbar, Avatar, StageStream, Poll },
  directives: { 'click-outside': clickOutsideDirective },
  data() {
    return {
      activeTab: 'info',
      selectedPoll: null,
      pollLoading: false,
      sponsorSearchInput: '',
      selectedSponsorCategory: null,
      sponsorCategoryMenuOpen: false,
      // In-flight email-document requests (by DocumentID) so the
      // "Email me" button can show a spinner + dedupe rapid clicks.
      sendingDocIds: []
    };
  },
  computed: {
    ...mapGetters('CurrentEvent', ['features', 'getEvent']),
    ...mapGetters('Sponsors', { allSponsorsRaw: 'getItems' }),
    ...mapGetters('Categories', {
      sponsorsCategories: 'getSponsorsCategories',
      allCategoriesRaw: 'getItems'
    }),
    allCategories() {
      const raw = this.allCategoriesRaw;
      if (Array.isArray(raw)) return raw;
      if (raw && typeof raw === 'object') return Object.values(raw);
      return [];
    },
    // Lookup id → normalized label key. Seeded from ALL categories
    // in the store so admin types other than SPONSOR_CATEGORY=3 still
    // resolve to a friendly label.
    sponsorCategoryIdToKey() {
      const map = new Map();
      const seed = (list) => {
        if (!Array.isArray(list)) return;
        list.forEach((c) => {
          if (!c) return;
          const id = this.pickCategoryId(c);
          const label = this.pickCategoryLabel(c);
          if (id && label) {
            map.set(String(id), String(label).trim().toLowerCase());
          }
        });
      };
      seed(this.allCategories);
      seed(this.sponsorsCategories);
      return map;
    },
    // Dropdown options derived from categories actually referenced
    // by sponsors in the list — no empty dropdown when the admin
    // hasn't categorized anyone.
    sponsorCategoryOptions() {
      const seen = new Map();
      const add = (c) => {
        if (c === null || c === undefined) return;
        let label = '';
        if (typeof c === 'string' || typeof c === 'number') {
          const str = String(c);
          const looked = this.sponsorCategoryIdToKey.get(str);
          if (looked) {
            const match = (this.allCategories || []).find(
              (cat) => String(this.pickCategoryId(cat)) === str
            );
            label = match ? this.pickCategoryLabel(match) : str;
          } else {
            label = str;
          }
        } else {
          label = this.pickCategoryLabel(c);
          if (!label) {
            const id = this.pickCategoryId(c);
            const looked = this.sponsorCategoryIdToKey.get(String(id));
            if (looked) {
              const match = (this.allCategories || []).find(
                (cat) => String(this.pickCategoryId(cat)) === String(id)
              );
              label = match ? this.pickCategoryLabel(match) : String(id);
            } else {
              label = String(id);
            }
          }
        }
        if (!label) return;
        const key = String(label).trim().toLowerCase();
        if (!key) return;
        if (!seen.has(key)) seen.set(key, { key, label: String(label) });
      };
      if (Array.isArray(this.sponsorsCategories)) {
        this.sponsorsCategories.forEach(add);
      }
      this.sortedSponsors.forEach((s) => {
        this.pickEntityCategories(s).forEach(add);
      });
      return Array.from(seen.values());
    },
    filteredOtherSponsors() {
      const q = (this.sponsorSearchInput || '').trim().toLowerCase();
      const catKey = this.selectedSponsorCategory
        ? String(this.selectedSponsorCategory).trim().toLowerCase()
        : null;
      return this.otherSponsors.filter((s) => {
        if (catKey && !this.sponsorCategoryKeys(s).includes(catKey)) {
          return false;
        }
        if (!q) return true;
        const haystack = [
          s.name || '',
          s.companyName || '',
          s.tagline || '',
          s.shortDescription || ''
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    },
    currentSponsorId() {
      return this.$route.params.sponsorId;
    },
    sortedSponsors() {
      const raw = this.allSponsorsRaw;
      const list = Array.isArray(raw) ? raw : Object.values(raw || {});
      return list
        .filter((s) => s && s.active !== false)
        .slice()
        .sort((a, b) => {
          const oa = parseInt(a && a.order) || 0;
          const ob = parseInt(b && b.order) || 0;
          return oa - ob;
        });
    },
    // Everyone except the sponsor whose page we're currently on —
    // surfaces the cross-sponsor switcher inside the right-hand
    // tab without ever offering a self-link.
    otherSponsors() {
      return this.sortedSponsors.filter(
        (s) => String(s.id || s.Id) !== String(this.currentSponsorId)
      );
    }
  },
  created() {
    // Prime the Sponsors + Categories stores so the right-side
    // switcher tab has data on a hard refresh of /sponsor/:id —
    // those stores are otherwise only populated by the lobby page.
    this.fetchAllSponsors();
    this.fetchAllCategories();
  },
  methods: {
    ...mapActions('Sponsors', { fetchAllSponsors: 'fetchItems' }),
    ...mapActions('Categories', { fetchAllCategories: 'fetchItems' }),
    // ── Category picker helpers (mirrors SponsorsListModal) ──
    pickCategoryLabel(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      const keys = [
        'label',
        'Label',
        'value',
        'Value',
        'name',
        'Name',
        'title',
        'Title',
        'caption',
        'Caption',
        'text',
        'Text',
        'categoryName',
        'CategoryName',
        'displayName',
        'DisplayName'
      ];
      for (const k of keys) {
        if (c[k] !== undefined && c[k] !== null && String(c[k]).trim() !== '') {
          return String(c[k]);
        }
      }
      return '';
    },
    pickCategoryId(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      const keys = [
        'id',
        'Id',
        'ID',
        'categoryId',
        'CategoryId',
        'categoryID',
        'CategoryID',
        'cid',
        'Cid'
      ];
      for (const k of keys) {
        if (c[k] !== undefined && c[k] !== null && String(c[k]).trim() !== '') {
          return String(c[k]);
        }
      }
      return '';
    },
    pickEntityCategories(entity) {
      if (!entity) return [];
      const keys = [
        'sponsorCategories',
        'SponsorCategories',
        'speakerCategories',
        'SpeakerCategories',
        'sponsorCategoryIds',
        'SponsorCategoryIds',
        'speakerCategoryIds',
        'SpeakerCategoryIds',
        'categoryIds',
        'CategoryIds',
        'categories',
        'Categories',
        'category',
        'Category',
        'categoryId',
        'CategoryId'
      ];
      for (const k of keys) {
        const v = entity[k];
        if (v === undefined || v === null) continue;
        if (Array.isArray(v)) return v;
        return [v];
      }
      return [];
    },
    sponsorCategoryKeys(sponsor) {
      if (!sponsor) return [];
      const raw = this.pickEntityCategories(sponsor);
      if (!raw.length) return [];
      const lookup = this.sponsorCategoryIdToKey;
      return raw
        .map((c) => {
          if (c === null || c === undefined) return '';
          const inlineLabel =
            typeof c === 'object' ? this.pickCategoryLabel(c) : '';
          if (inlineLabel && typeof c === 'object') {
            return String(inlineLabel).trim().toLowerCase();
          }
          const id = this.pickCategoryId(c);
          if (!id) {
            const scalar =
              typeof c === 'string' || typeof c === 'number' ? String(c) : '';
            return scalar ? scalar.trim().toLowerCase() : '';
          }
          const looked = lookup.get(String(id));
          return looked || String(id).trim().toLowerCase();
        })
        .filter(Boolean);
    },
    setSponsorCategory(key) {
      this.selectedSponsorCategory = key;
      this.sponsorCategoryMenuOpen = false;
    },
    closeSponsorCategoryMenu() {
      this.sponsorCategoryMenuOpen = false;
    },
    isCurrentSponsor(s) {
      return String(s && (s.id || s.Id)) === String(this.currentSponsorId);
    },
    hasLiveStream(s) {
      if (!s) return false;
      return !!(
        s.streamUrl ||
        s.StreamUrl ||
        s.liveUrl ||
        s.LiveUrl ||
        s.liveStreamUrl ||
        s.LiveStreamUrl
      );
    },
    switchToSponsor(s) {
      if (!s) return;
      const id = s.id || s.Id;
      if (!id || this.isCurrentSponsor(s)) return;
      this.$router.push({ name: 'sponsor', params: { sponsorId: id } });
    },
    onSpTabKeydown(e) {
      const tabs = ['info', 'docs', 'polls', 'people', 'sponsors'];
      const idx = tabs.indexOf(this.activeTab);
      let next = idx;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        next = (idx + 1) % tabs.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        next = (idx - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        next = 0;
      } else if (e.key === 'End') {
        next = tabs.length - 1;
      } else {
        return;
      }
      e.preventDefault();
      this.activeTab = tabs[next];
      this.$nextTick(() => {
        const btn = document.getElementById(`sp-tab-${tabs[next]}`);
        if (btn) btn.focus();
      });
    },
    goBack() {
      // Prefer history; fall back to lobby
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: 'lobby' });
      }
    },
    // Admin-added links → icon descriptors (brand icons / globe).
    socialLinkStyle(l) {
      return l && l.color ? { color: l.color } : null;
    },
    socialLinksFor(sponsor) {
      const links = (sponsor && sponsor.links) || [];
      return links
        .map((l) => socialLinkMeta(l && (l.url || l.value || l)))
        .filter((l) => l.href);
    },
    normalizeUrl(url) {
      if (!url) return url;
      const trimmed = String(url).trim();
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      return `https://${trimmed}`;
    },
    sendContactInfo() {
      const sponsorId = this.$route.params.sponsorId;
      createAnalyticsEvent(
        this.$gtag,
        'ClickOnSendContactInfo',
        'Sponsors',
        sponsorId
      );
      ApiClient.sendContactInfoToSponsor(sponsorId).then(() => {
        this.$notify({
          group: 'default',
          title: this.$t('Views.Sponsor.successMessage')
        });
      });
    },

    // ── Documents helpers ──────────────────────────────────────────────
    docsList(documents) {
      return Array.isArray(documents) ? documents : [];
    },
    docUrl(doc) {
      if (!doc) return '';
      const raw =
        doc.Documentfile ||
        doc.DocumentFile ||
        doc.documentfile ||
        doc.url ||
        '';
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return raw;
      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '');
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return `${base}${path}`;
    },
    previewDocument(doc) {
      const url = this.docUrl(doc);
      if (!url) return;
      window.open(url, '_blank', 'noopener');
    },
    downloadDocument(doc) {
      const url = this.docUrl(doc);
      if (!url) return;
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.Name || '';
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    isSendingDoc(doc) {
      return !!(doc && this.sendingDocIds.indexOf(doc.DocumentID) !== -1);
    },
    async emailDocument(doc) {
      const id = doc && doc.DocumentID;
      if (!id) return;
      if (this.sendingDocIds.indexOf(id) !== -1) return;
      this.sendingDocIds = [...this.sendingDocIds, id];
      const docName = doc.Name || this.$t('Views.Sponsor.documentFallback');
      try {
        // Preferred per-doc endpoint; falls back to sendAllDocuments
        // on 404/405 so the participant still gets the file even if
        // the backend hasn't shipped the dedicated route yet.
        await ApiClient.sendDocumentByEmail(id);
        this.$notify({
          group: 'default',
          type: 'success',
          title: this.$t('Views.Sponsor.documentSent'),
          text: this.$t('Views.Sponsor.documentOnItsWay', { name: docName })
        });
      } catch (e) {
        const status = e && e.response && e.response.status;
        const endpointMissing = status === 404 || status === 405;
        if (endpointMissing) {
          try {
            await ApiClient.sendAllDocuments();
            this.$notify({
              group: 'default',
              type: 'success',
              duration: 5000,
              title: this.$t('Views.Sponsor.documentsSent'),
              text: this.$t('Views.Sponsor.documentOnItsWayAll', {
                name: docName
              })
            });
            return;
          } catch (fallbackErr) {
            /* fall through to the generic error toast */
          }
        }
        this.$notify({
          group: 'default',
          type: 'error',
          duration: 6000,
          title: this.$t('Views.Sponsor.documentSendFailed'),
          text: this.$t('Views.Sponsor.documentSendFailedHint')
        });
      } finally {
        this.sendingDocIds = this.sendingDocIds.filter((x) => x !== id);
      }
    },
    docExt(doc) {
      const name = doc && doc.Name;
      if (!name) return 'FILE';
      const m = name.match(/\.([^.]+)$/);
      return m ? m[1].toUpperCase() : 'FILE';
    },
    docIcon(doc) {
      const ext = this.docExt(doc).toLowerCase();
      if (ext === 'pdf') return 'fas fa-file-pdf';
      if (['ppt', 'pptx'].includes(ext)) return 'fas fa-file-powerpoint';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'fas fa-file-excel';
      if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word';
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext))
        return 'far fa-file-image';
      return 'far fa-file';
    },
    docIconClass(doc) {
      const ext = this.docExt(doc).toLowerCase();
      if (ext === 'pdf') return 'sp-doc-card__icon--red';
      if (['ppt', 'pptx'].includes(ext)) return 'sp-doc-card__icon--orange';
      if (['xls', 'xlsx', 'csv'].includes(ext))
        return 'sp-doc-card__icon--green';
      if (['doc', 'docx'].includes(ext)) return 'sp-doc-card__icon--blue';
      return 'sp-doc-card__icon--gray';
    },
    formatBytes(bytes) {
      if (!bytes) return '';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      let n = Number(bytes);
      while (n >= 1024 && i < units.length - 1) {
        n /= 1024;
        i++;
      }
      return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
    },

    handleNamedPolls(pollType) {
      switch (pollType) {
        case 0:
        case 1:
          return 'poll';
        case 2:
          return 'quiz';
        case 3:
          return 'treasure_hunt';
        default:
          return 'poll';
      }
    },
    openSponsorPoll(poll) {
      if (!poll) return;
      const id = poll.id || poll.value;
      if (!id) return;
      this.pollLoading = true;
      ApiClient.getPoll(id, false)
        .then((response) => {
          if (response && response.data) {
            // Commit Sponsor's promotedPage so the inner PollDataProvider
            // (container="Sponsor") picks up the correct page/id when the
            // Poll component mounts.
            this.$store.commit('Sponsor/setPromotedPage', {
              page: this.handleNamedPolls(response.data.pollType),
              id: response.data.id
            });
            this.selectedPoll = response.data;
          }
        })
        .finally(() => {
          this.pollLoading = false;
        });
    },
    closeSponsorPoll() {
      this.selectedPoll = null;
      this.$store.commit('Sponsor/setPromotedPage', { page: null, id: null });
    }
  }
};
</script>

<style scoped lang="scss">
.sponsor-page {
  font-family: 'Poppins', sans-serif;

  *:focus-visible {
    outline: 2px solid var(--color-primary, #047873);
    outline-offset: 2px;
  }
}

.sp-wrap {
  // No max-width — match the LiveStream page so the sponsor view
  // uses the full viewport width with just modest side padding,
  // instead of being centered with empty margins on wide monitors.
  width: 100%;
  margin: 0 auto;
  padding: 20px 28px 40px;

  // Mobile: the side card's icon strip is the last thing on the page,
  // and the fixed bottom-nav pill (16px offset + ~56px tall) floats over
  // it. App.vue's `body { padding-bottom }` doesn't help here: body is
  // `height: 100%`, so that padding sits at the viewport edge rather than
  // after the content. Reserve the clearance on the page itself, as
  // Program and Lobby do.
  @media (max-width: 640px) {
    padding: 14px 14px 104px;
  }
}

.sp-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  // Anchor the entire grid to the viewport on desktop so the page
  // itself never scrolls — internal regions (about / tabs) handle
  // their own overflow. Mobile stacks vertically and lets the
  // page scroll naturally.
  height: calc(100vh - 140px);
  min-height: 480px;
  align-items: stretch;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1fr) 380px;
    gap: 24px;
  }

  @media (max-width: 1023px) {
    height: auto;
    min-height: 0;
  }
}

// LEFT column — header on top, player flexes to fill the rest,
// optional About section sits below the player but is content-
// sized (no flex grow) so the column never overflows the grid.
.sp-left {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 1023px) {
    overflow: visible;
  }
}

// ── Header ───────────────────────────────────────────────────────────
.sp-header {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 14px 18px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);

  &__back {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s, border-color 0.15s;

    &:hover {
      background: #f9fafb;
      color: var(--color-primary, #047873);
      border-color: var(--color-primary, #047873);
    }
  }

  &__logo-wrap {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 6px;
    flex-shrink: 0;
  }

  &__logo {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__title-block {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sub {
    font-size: 13px;
    color: #6b7280;
    margin: 4px 0 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i {
      color: #6b7280;
    }
  }

  &__action {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 10px 18px;
    border-radius: 10px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    border: none;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: var(--color-primary-hover, #036663);
    }

    @media (max-width: 640px) {
      padding: 10px 12px;
    }
  }

  &__action-label {
    @media (max-width: 640px) {
      display: none;
    }
  }
}

// ── Player ───────────────────────────────────────────────────────────
.sp-player {
  position: relative;
  background: #000000;
  border-radius: 14px;
  overflow: hidden;
  // Desktop: fills the remaining vertical space inside `.sp-left`
  // (which is grid-bounded), so the page itself never scrolls.
  // Mobile: revert to a natural 16:9 banner since the column
  // stacks and the page scrolls normally.
  flex: 1 1 0;
  min-height: 0;
  border: 1px solid #e5e7eb;

  @media (max-width: 1023px) {
    flex: 0 0 auto;
    aspect-ratio: 16 / 9;
  }

  &__video {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__stream {
    width: 100%;
    height: 100%;
  }

  &__placeholder {
    text-align: center;
    color: #6b7280;
  }

  &__eye {
    font-size: 56px;
    margin-bottom: 8px;
  }

  &__active-text {
    font-size: 14px;
  }

  &__logo-fallback {
    background: #ffffff;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;

    img {
      max-width: 60%;
      max-height: 60%;
      object-fit: contain;
    }
  }
}

// ── About ────────────────────────────────────────────────────────────
.sp-about {
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 18px 20px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
  // Cap the desktop about block so the column above (header +
  // player) stays visible — the body scrolls internally when the
  // sponsor's description overflows.
  flex: 0 1 auto;
  min-height: 0;
  max-height: 30%;
  overflow-y: auto;
  scrollbar-width: thin;

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 10px;
  }

  &__body {
    font-size: 14px;
    color: #374151;
    line-height: 1.65;
  }

  @media (max-width: 1023px) {
    max-height: none;
    overflow-y: visible;
  }
}

::v-deep .sp-about__body.html-content {
  p {
    margin: 0 0 10px;
  }
  a {
    color: var(--color-primary, #047873);
    text-decoration: underline;
  }
  ul,
  ol {
    padding-left: 20px;
    margin-bottom: 10px;
  }
}

// ── Sidebar / Tabs ───────────────────────────────────────────────────
.sp-side {
  position: relative;
  isolation: isolate;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 480px;
  max-height: calc(100vh - 120px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  // Panel content occupies the LEFT, icon rail sits on the RIGHT.
  > .sp-side__main {
    order: 0;
  }
  > .sp-icon-bar {
    order: 1;
  }

  &__main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #ffffff;
    border-right: 1px solid #f1f2f4;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
  }

  @media (max-width: 1100px) {
    flex-direction: column;

    &__main {
      border-right: none;
      border-bottom: 1px solid #f1f2f4;
    }
  }

  @media (max-width: 640px) {
    border-radius: 12px;
  }
}

// ── Vertical icon rail (right edge of the side card) ─────────────────
// Mirrors LiveStream's `.ls-icon-bar`. Each icon is a 44×44 round-
// square button; hovering reveals a midnight pill tooltip floating
// to the left of the icon (pointing inward at the panel content).
.sp-icon-bar {
  flex-shrink: 0;
  width: 64px;
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 16px 0;
  overflow: visible;

  &__btn {
    position: relative;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-size: 16px;
    font-family: 'Poppins', sans-serif;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s, transform 0.15s;

    i {
      font-size: 16px;
      color: inherit;
      transition: color 0.15s;
    }

    &:hover,
    &:focus-visible {
      background: rgba(0, 24, 56, 0.08);
      color: #001838;
      outline: none;
      transform: translateX(-2px);
    }

    &--active {
      background: var(--color-primary, #047873);
      color: #ffffff;

      &:hover,
      &:focus-visible {
        background: var(--color-primary, #047873);
        color: #ffffff;
        transform: none;
      }
    }
  }

  &__label {
    position: absolute;
    right: calc(100% + 10px);
    top: 50%;
    transform: translateY(-50%) translateX(4px);
    background: #001838;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 9999px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s, transform 0.15s;
    z-index: 20;
    font-family: 'Poppins', sans-serif;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &::before {
      content: '';
      position: absolute;
      right: -4px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
      width: 8px;
      height: 8px;
      background: #001838;
    }
  }

  &__btn:hover &__label,
  &__btn:focus-visible &__label {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }

  // Small count badge — anchored to the top-right corner of the icon
  // button, replaces the old inline `.sp-tab__badge` chip.
  &__badge {
    position: absolute;
    top: 4px;
    right: 4px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    padding: 2px 5px;
    border-radius: 9999px;
    min-width: 16px;
    text-align: center;
    box-shadow: 0 0 0 2px #ffffff;
  }

  &__btn--active &__badge {
    background: #ffffff;
    color: var(--color-primary, #047873);
    box-shadow: 0 0 0 2px var(--color-primary, #047873);
  }

  // ── Mobile: flip to a horizontal strip below the panel content ──
  @media (max-width: 1100px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    padding: 8px 12px;
    background: #ffffff;
    border-top: 1px solid #f1f2f4;
    gap: 4px;

    &__btn {
      flex: 1;
      max-width: 96px;
      height: 40px;
      border-radius: 10px;
    }

    // Tooltip pill doesn't make sense in a horizontal strip — hide.
    &__label {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
      opacity: 0;
      &::before {
        display: none;
      }
    }

    &__btn:hover &__label,
    &__btn:focus-visible &__label {
      opacity: 0;
      transform: none;
    }
  }
}

.sp-panel {
  display: flex;
  flex-direction: column;
}

// ── Info panel ───────────────────────────────────────────────────────
.sp-info {
  padding: 0;

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    padding: 16px 18px 12px;
    border-bottom: 1px solid #f3f4f6;
    background: #ffffff;
  }

  &__list {
    padding: 16px 18px 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
}

.sp-info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;

    &--teal {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
    }
    &--green {
      background: #dcfce7;
      color: #15803d;
    }
    &--orange {
      background: #fef3c7;
      color: #d97706;
    }
    &--purple {
      background: #f3e8ff;
      color: #7e22ce;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 2px;
  }

  &__value {
    font-size: 13px;
    color: #374151;
    word-break: break-word;
  }

  &__link {
    font-size: 13px;
    color: var(--color-primary, #047873);
    text-decoration: none;
    word-break: break-word;

    &:hover {
      text-decoration: underline;
    }
  }
}

// ── Documents header / cards ─────────────────────────────────────────
.sp-docs-header {
  padding: 16px 18px 12px;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;

  @media (max-width: 1100px) {
    padding-right: 64px;
  }
  @media (max-width: 640px) {
    padding-right: 54px;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__sub {
    font-size: 12px;
    color: var(--color-primary, #047873);
    font-weight: 500;
    margin: 4px 0 0;
  }
}

.sp-docs-list {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sp-doc-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  background: #ffffff;

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 10px;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;

    &--red {
      background: #fee2e2;
      color: #dc2626;
    }
    &--orange {
      background: #fef3c7;
      color: #d97706;
    }
    &--green {
      background: #dcfce7;
      color: #15803d;
    }
    &--blue {
      background: #dbeafe;
      color: #1d4ed8;
    }
    &--gray {
      background: #f3f4f6;
      color: #6b7280;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Small inline envelope icon — emails this doc to the participant.
  &__mail {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      background: rgba(4, 120, 115, 0.1);
      color: var(--color-primary, #047873);
      outline: none;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    &--sending {
      color: var(--color-primary, #047873);
    }
  }

  &__meta {
    font-size: 11px;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__type {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__sep {
    color: #d1d5db;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}

.sp-doc-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  i {
    font-size: 11px;
  }

  &--primary {
    background: var(--color-primary, #047873);
    color: #ffffff;

    &:hover {
      background: var(--color-primary-hover, #036663);
    }
  }

  &--secondary {
    background: #ffffff;
    color: #374151;
    border: 1.5px solid #e5e7eb;

    &:hover {
      border-color: var(--color-primary, #047873);
      color: var(--color-primary, #047873);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// ── Polls list ───────────────────────────────────────────────────────
.sp-polls-list {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sp-docs-header--with-back {
  display: flex;
  align-items: center;
  gap: 10px;
}

// Icon-only back arrow — mirrors LiveStream's `.ls-section-back` so
// the sponsor page's right-side panel headers read identically.
.sp-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, transform 0.15s;

  i {
    font-size: 13px;
  }

  &:hover,
  &:focus-visible {
    background: #f3f4f6;
    color: var(--color-primary, #047873);
    outline: none;
    transform: translateX(-1px);
  }
}

.sp-poll-body {
  padding: 16px;
  flex: 1;
  overflow-y: auto;

  ::v-deep {
    // Outer wrappers — drop loose tailwind padding/widths so the poll
    // sits flush inside our card panel
    .p-4 {
      padding: 0 !important;
    }
    .w-full.lg\:w-3\/5,
    .lg\:w-3\/5,
    .xl\:w-full {
      width: 100% !important;
    }

    h1,
    h3 {
      font-family: 'Poppins', sans-serif;
      font-size: 15px;
      font-weight: 700;
      color: #111827;
      margin: 0 0 10px;
      line-height: 1.4;
    }

    .html-content,
    .text-sm {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      color: #6b7280;
      line-height: 1.5;
      margin-bottom: 12px;
    }

    textarea {
      width: 100%;
      min-height: 96px;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #ffffff;
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      color: #111827;
      resize: vertical;
      transition: border-color 0.15s, box-shadow 0.15s;

      &::placeholder {
        color: #6b7280;
      }
      &:focus {
        outline: none;
        border-color: var(--color-primary, #047873);
        box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.12);
      }
    }

    // Multiple-choice / quiz options
    .button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 12px 14px;
      margin: 8px 0 0 !important;
      border: 1.5px solid #e5e7eb !important;
      border-radius: 10px;
      background: #ffffff !important;
      color: #111827 !important;
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s, color 0.15s;

      &:hover {
        border-color: var(--color-primary, #047873) !important;
        background: #f0fbf9 !important;
      }

      &.bg-primary-bg,
      &.font-bold {
        border-color: var(--color-primary, #047873) !important;
        background: var(--color-primary, #047873) !important;
        color: #ffffff !important;
      }

      span {
        font-weight: inherit;
      }
    }

    // Action row
    .flex.justify-between {
      gap: 8px;
      margin-top: 16px;
      flex-wrap: wrap;
    }

    .button-primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 40px;
      padding: 9px 18px;
      border-radius: 10px;
      background: var(--color-primary, #047873) !important;
      color: #ffffff !important;
      border: none !important;
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      width: auto;
      margin: 0 !important;

      &:hover {
        background: var(--color-primary-hover, #036663) !important;
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .button-secondary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 40px;
      padding: 9px 16px;
      border-radius: 10px;
      background: #ffffff !important;
      color: #374151 !important;
      border: 1.5px solid #e5e7eb !important;
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      width: auto;
      margin: 0 !important;

      &:hover {
        border-color: var(--color-primary, #047873) !important;
        color: var(--color-primary, #047873) !important;
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

.sp-poll-card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    background: #f9fafb;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #e6f5f4;
    color: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
  }

  &__sub {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
  }

  &__chevron {
    font-size: 12px;
    color: #6b7280;
    flex-shrink: 0;
  }
}

// ── People list ──────────────────────────────────────────────────────
.sp-people-list {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sp-person-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;

  &__avatar {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__role {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// ── Sponsor switcher filters (search + category) ─────────────────────
.sp-sponsor-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px 4px;

  &__search-wrap {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 13px;
  }

  &__search {
    width: 100%;
    height: 38px;
    padding: 0 16px 0 38px;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    font-size: 13px;
    background: #f9fafb;
    outline: none;
    color: #111827;
    font-family: inherit;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      border-color: #d1d5db;
      background: #ffffff;
    }
  }

  // Compact filter icon button — sits beside the search bar in
  // place of the old "All categories" select. Tapping it pops the
  // category list anchored below.
  &__category-wrap {
    position: relative;
    flex: 0 0 auto;
  }

  &__filter-btn {
    position: relative;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #374151;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s, color 0.15s;

    i {
      font-size: 13px;
    }

    &:hover,
    &:focus-visible {
      border-color: var(--color-primary, #047873);
      color: var(--color-primary, #047873);
      outline: none;
    }

    &--active {
      border-color: var(--color-primary, #047873);
      background: rgba(4, 120, 115, 0.08);
      color: var(--color-primary, #047873);
    }
  }

  // Small teal dot indicating an active category filter.
  &__filter-dot {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    box-shadow: 0 0 0 2px #ffffff;
  }

  &__category-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 20;
    min-width: 200px;
    max-height: 280px;
    overflow-y: auto;
    margin: 0;
    padding: 6px;
    list-style: none;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  }

  &__category-item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #111827;
    font-size: 13px;
    font-weight: 500;
    font-family: 'Poppins', sans-serif;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    i {
      color: var(--color-primary, #047873);
      font-size: 11px;
    }

    &:hover,
    &:focus-visible {
      background: #f3f4f6;
      outline: none;
    }

    &--selected {
      color: var(--color-primary, #047873);
      font-weight: 600;
    }
  }
}

.sp-cat-menu-enter-active,
.sp-cat-menu-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.sp-cat-menu-enter,
.sp-cat-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

// ── Cross-sponsor switcher list ──────────────────────────────────────
.sp-sponsor-list {
  display: flex;
  flex-direction: column;
  padding: 6px 6px 8px;
}

.sp-sponsor-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s;
  width: 100%;

  & + & {
    border-top: 1px solid #f3f4f6;
  }

  &:hover,
  &:focus-visible {
    background: #f3f4f6;
    outline: none;
  }

  &--active {
    background: rgba(4, 120, 115, 0.08);
    cursor: default;

    &:hover {
      background: rgba(4, 120, 115, 0.08);
    }

    .sp-sponsor-row__name {
      color: var(--color-primary, #047873);
    }
  }

  &__logo-wrap {
    width: 48px;
    height: 40px;
    border-radius: 8px;
    background: #f9fafb;
    border: 1px solid #f1f2f4;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__logo {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__logo-fallback {
    color: #9ca3af;
    font-size: 16px;
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__tagline {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: #16a34a;
  }

  &__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #16a34a;
    animation: sp-live-pulse 1.5s ease-in-out infinite;
    flex-shrink: 0;
  }

  &__chevron {
    color: #d1d5db;
    font-size: 12px;
    flex-shrink: 0;
  }
}

@keyframes sp-live-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.6;
  }
}

// ── Empty state ──────────────────────────────────────────────────────
.sp-empty {
  text-align: center;
  padding: 32px 18px;
  color: #6b7280;

  p {
    font-size: 13px;
    margin: 0;
  }
}

.sp-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.sp-social {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 4px;
}
.sp-social__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f3f4f6;
  color: #374151;
  font-size: 17px;
  text-decoration: none;
  transition: background 0.15s, color 0.15s, transform 0.15s;

  &:hover {
    background: #e5e7eb;
    transform: translateY(-1px);
  }
}
</style>
